import { apiFetch } from "./client";
import type { RoleRef } from "./access";

/** The user's own UI theme (V13); SYSTEM follows the OS / browser setting. */
export type UiTheme = "LIGHT" | "DARK" | "SYSTEM";

export type UserSummary = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
  theme: UiTheme;
  /** Display only — decisions go through `permissions` (sprint 7). */
  roles: RoleRef[];
};

export type MeResponse = {
  user: UserSummary;
  permissions: string[];
  /** Positions whose branches the structure functions are narrowed to (sprint 7 §12); empty = everywhere. */
  structureScope: string[];
};

export type InvitationInfo = {
  email: string;
  roles: RoleRef[];
  expiresAt: string;
};

export const authApi = {
  login: (email: string, password: string, rememberMe?: boolean) =>
    apiFetch<UserSummary>("/auth/login", {
      method: "POST",
      body: { email, password, rememberMe: !!rememberMe },
      skipRefresh: true,
    }),

  logout: () =>
    apiFetch<void>("/auth/logout", { method: "POST", skipRefresh: true }),

  refresh: () =>
    apiFetch<UserSummary>("/auth/refresh", { method: "POST", skipRefresh: true }),

  me: () => apiFetch<MeResponse>("/auth/me"),

  forgotPassword: (email: string) =>
    apiFetch<void>("/auth/forgot-password", {
      method: "POST",
      body: { email },
      skipRefresh: true,
    }),

  resetPassword: (token: string, newPassword: string) =>
    apiFetch<void>("/auth/reset-password", {
      method: "POST",
      body: { token, newPassword },
      skipRefresh: true,
    }),

  inspectInvitation: (token: string) =>
    apiFetch<InvitationInfo>(`/auth/invitations/${encodeURIComponent(token)}`, {
      skipRefresh: true,
    }),

  acceptInvitation: (
    token: string,
    body: { password: string; firstName: string; lastName: string; phone?: string },
  ) =>
    apiFetch<UserSummary>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
      method: "POST",
      body,
      skipRefresh: true,
    }),

  /** Self-service edit of the caller's own profile (phone for now). */
  updateMe: (body: { phone?: string | null }) =>
    apiFetch<UserSummary>("/me", { method: "PATCH", body }),

  /** The caller's own light / dark / system choice (204). */
  setTheme: (theme: UiTheme) =>
    apiFetch<void>("/me/theme", { method: "PUT", body: { theme } }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>("/me/password", {
      method: "POST",
      body: { currentPassword, newPassword },
    }),

  listSessions: () => apiFetch<SessionView[]>("/me/sessions"),

  revokeSession: (id: string) =>
    apiFetch<void>(`/me/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),

  presignAvatar: (contentType: string, sizeBytes: number) =>
    apiFetch<AvatarPresignResponse>("/me/avatar/presign", {
      method: "POST",
      body: { contentType, sizeBytes },
    }),

  confirmAvatar: (objectKey: string) =>
    apiFetch<{ avatarUrl: string }>("/me/avatar/confirm", {
      method: "POST",
      body: { objectKey },
    }),

  deleteAvatar: () =>
    apiFetch<void>("/me/avatar", { method: "DELETE" }),
};

export type AvatarPresignResponse = {
  url: string;
  objectKey: string;
};

export type SessionView = {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  issuedAt: string;
  lastUsedAt: string | null;
  current: boolean;
};
